import type { Entitlement } from '@experiences/constants';
import { Entitlements } from '@experiences/constants';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { UiSuspensefulOutlet } from '@experiences/ui-common';
import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { routePaths } from '../../common/constants/routePaths';
import { useOrganizationName } from '../../common/hooks/useOrganizationName';
import BreadcrumbProvider from '../../common/providers/BreadcrumbProvider';
import CheckGuard from '../../container/helpers/CheckGuard';
import type { UiRouteObject } from '../../container/routeConfigs/UiRouteObject';
import { useEntitlements } from '../entitlements/EntitlementsContextProvider';
import { AddEditConfigurationComponent } from './llmConfigurations/AddEditConfigurationComponent';
import { AddEditConfigurationComponentV2 } from './llmConfigurations/AddEditConfigurationComponentV2';

const AddEditConfigurationComponentWrapper: React.FC = () => {
    const enableV2 = useFeatureFlagValue(Features.EnableAiTrustLayerByoLlmConfigurationsV2.name);
    return enableV2 ? <AddEditConfigurationComponentV2 /> : <AddEditConfigurationComponent />;
};

const AiTrustLayerComponent = React.lazy(() =>
    import('./AiTrustLayerComponent').then(module => ({ default: module.AiTrustLayerComponent }))
);

const AiTrustSettingsProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { formatMessage: translate } = useIntl();
    const organizationName = useOrganizationName();

    const EnableGovernanceModuleFederation = useFeatureFlagValue(Features.EnableGovernanceModuleFederation.name);

    const breadCrumbLinks = useMemo(() => [
        {
            index: 0,
            link: routePaths.admin,
            name: organizationName,
        },
        {
            index: 1,
            link: routePaths.adminAiTrustLayer,
            name: translate({ id: 'CLIENT_AI_TRUST_LAYER' }),
        },
    ], [ organizationName, translate ]);

    return (
        <BreadcrumbProvider breadcrumbs={breadCrumbLinks}>
            {CheckGuard(EnableGovernanceModuleFederation, <>
                {children}
            </>)}
        </BreadcrumbProvider>
    );
};

type TabGuardProp = {
    featureFlagName?: string;
    entitlementName?: Entitlement;
};

const AiTrustLayerTabSettingsProvider: React.FC<PropsWithChildren<TabGuardProp>> = ({
    featureFlagName, entitlementName, children,
}) => {
    const { hasEntitlement } = useEntitlements();
    const featureFlagValue = useFeatureFlagValue(featureFlagName ?? '');
    const isPricingRevampEnabled = useFeatureFlagValue(Features.EnableAiTrustLayerPricingRevamp.name);
    const guardValue = useMemo(() => {
        let result = true;

        if (featureFlagName) {
            result = result && featureFlagValue;
        }

        // do not enforce check for 'ignored' pricing revamp entitlements (LlmOps, Governance)
        if (entitlementName && (featureFlagName === Features.EnableAiTrustLayerByoLlm.name || isPricingRevampEnabled)) {
            result = result && hasEntitlement(entitlementName);
        }

        return result;
    }, [ entitlementName, featureFlagName, featureFlagValue, hasEntitlement, isPricingRevampEnabled ]);

    return (
        CheckGuard(guardValue, <>
            {children}
        </>)
    );
};

export const AiTrustRoutes: UiRouteObject = {
    path: routePaths.adminAiTrustLayer,
    element: (
        <AiTrustSettingsProvider>
            <UiSuspensefulOutlet />
        </AiTrustSettingsProvider>
    ),
    explicitEnvironments: [ 'cloud', 'k8s', 'dedicated' ],
    children: [
        {
            index: true,
            element: <AiTrustLayerComponent />,
        },
        {
            path: routePaths.adminAiTrustLayerUsage,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerUsage.name}
                    entitlementName={Entitlements.AiTrustLayerShowLlmOps}>
                    <AiTrustLayerComponent />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
        {
            path: routePaths.adminAiTrustLayerAudit,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerAudit.name}
                    entitlementName={Entitlements.AiTrustLayerShowLlmOps}>
                    <AiTrustLayerComponent />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
        {
            path: routePaths.adminAiTrustLayerGovernance,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerGovernance.name}
                    entitlementName={Entitlements.AiTrustLayerShowGovernance}>
                    <AiTrustLayerComponent />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
        {
            path: routePaths.adminAiTrustLayerA4E,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerA4E.name}>
                    <AiTrustLayerComponent />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
        {
            path: routePaths.adminAiTrustLayerContextGrounding,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerContextGrounding.name}>
                    <AiTrustLayerComponent />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
        {
            path: routePaths.adminAiTrustLayerLlmConfigurations,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerByoLlm.name}
                    entitlementName={Entitlements.AiTrustLayerByoLlm}>
                    <AiTrustLayerComponent />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
        {
            path: routePaths.adminAiTrustLayerLlmConfigurationsAdd,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerByoLlm.name}
                    entitlementName={Entitlements.AiTrustLayerByoLlm}>
                    <AddEditConfigurationComponentWrapper />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
        {
            path: routePaths.adminAiTrustLayerLlmConfigurationsView,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerByoLlmConfigurationsV2.name}
                    entitlementName={Entitlements.AiTrustLayerByoLlm}>
                    <AddEditConfigurationComponent
                        readOnly
                    />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
        {
            path: routePaths.adminAiTrustLayerLlmConfigurationsEdit,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerByoLlm.name}
                    entitlementName={Entitlements.AiTrustLayerByoLlm}>
                    <AddEditConfigurationComponentWrapper />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
        {
            path: routePaths.adminAiTrustLayerOtelConfigurations,
            element: (
                <AiTrustLayerTabSettingsProvider
                    featureFlagName={Features.EnableAiTrustLayerOtelConfigurations.name}
                    entitlementName={Entitlements.AiTrustLayerShowLlmOps}>
                    <AiTrustLayerComponent />
                </AiTrustLayerTabSettingsProvider>
            ),
        },
    ],
};
